import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Array, AsyncStateN } from '@blade/std-lib'
import { useAppRoutes } from '@root/features/routing'
import { useTranslation } from '@root/shared/localization'
import { InfoMessage, PnlChartIcon } from '@root/shared/ui-kit'

import { PnlOwner } from '../../model/pnl-relation'

import { PnlChartLoading } from './pnl-chart-loading'
import { PnlInnerChart } from './pnl-inner-chart'

import type { UserPnlChartModel } from '../../atoms'
import type { PnlChartDivision } from '../../model'
import type { Action, Atom } from '@reatom/framework'
import type { Trend } from '@root/entities/trend'
import type { UserTradingAnalyticsPriceType } from '@root/features/user/user-trading-analytics/models/user-trading-analytics-price-type'
import type { RequestAtom } from '@root/shared/lib/reatom/reatom-request'
import type { TransKey } from '@root/shared/localization'
import type { ClassNameProps } from '@root/shared/react'

export type PnlChartPlacement = 'portfolio' | 'chart' | 'profile'

type Props = ClassNameProps & {
  model: RequestAtom.AsAtom<UserPnlChartModel> & {
    division: Atom<PnlChartDivision.PnlChartDivision>
    retryAction: Action
    priceType: Atom<UserTradingAnalyticsPriceType>
  }
  trend: Trend.Trend
  owner: PnlOwner
  renderEmptyMessage?: () => React.ReactElement<typeof InfoMessage>
}

const OwnerToTitleMap: Record<PnlOwner, TransKey> = {
  [PnlOwner.CurrentUser]: 'portfolio.pnl-chart.error-title',
  [PnlOwner.AnotherUsers]: 'portfolio.pnl-chart.error-title.another-user',
}

const OwnerToDescriptionMap: Record<PnlOwner, TransKey> = {
  [PnlOwner.CurrentUser]: 'portfolio.pnl-chart.error-description',
  [PnlOwner.AnotherUsers]: 'portfolio.pnl-chart.error-description.another-user',
}

export const PnlChart: React.FC<Props> = ({
  model,
  className,
  renderEmptyMessage,
  trend,
  owner,
}) => {
  const { t } = useTranslation()
  const { navigateToRoot } = useAppRoutes()

  const [division] = useAtom(model.division)
  const [priceType] = useAtom(model.priceType)
  const [dataState] = useAtom(model)
  const retry = useAction(model.retryAction)

  const handleExploreTokens = () => navigateToRoot()

  return (
    <Root className={className}>
      {AsyncStateN.matchWaiting(dataState, {
        Error: () => <PnlInfoMessage onRetry={() => retry()} intent="error" />,
        Waiting: () => <PnlChartLoading priceType={priceType} />,
        Success: (data) => {
          if (Array.isNotEmpty(data)) {
            return (
              <PnlInnerChart
                division={division}
                data={data}
                priceType={priceType}
                trend={trend}
              />
            )
          }

          return (
            renderEmptyMessage?.() ?? (
              <PnlInfoMessage
                icon={<PnlChartIcon />}
                intent="empty"
                variant="bold"
                retryText={t('common.explore-tokens')}
                title={t(OwnerToTitleMap[owner])}
                description={t(OwnerToDescriptionMap[owner])}
                onRetry={handleExploreTokens}
              />
            )
          )
        },
      })}
    </Root>
  )
}

const Root = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;

  display: grid;
`

const PnlInfoMessage = styled(InfoMessage)`
  margin-top: -30px;
`
